// apps/share-web/src/setlist-screen/boot.ts
//
// Единый сетлист (фазы 1-4) — маршрут `/setlist/<id>` (аудио), `/setlist/w/<uuid>`
// (старая ссылка слов) и `/s/<uuid>[?g=]` (гость/участник, задача 12) сходятся
// в один вид (setlist-screen/route.ts собирает исход:
// needs-auth/not-found/error/redirect/mounted). Вынесено СЮДА из main.ts
// (рефакторинг после ревью задачи D3 — дисциплина роста: монолит несёт
// только вызов). Поведение не менялось, только адрес: тело
// `bootUnifiedSetlistRoute` — дословно бывший блок `boot()`.
//
// `enterFullBleedView`/`mountFullBleedHeader`/`exitFullBleedView` остаются
// экспортами main.ts (общий шов всех full-bleed маршрутов — /login, /library,
// /dashboard, /words — не только сетлиста) и приходят сюда импортом. Обе
// стороны — function-декларации (hoisted) вызываются только ВНУТРИ чужих
// функций, а не на верхнем уровне модуля, так что цикл main.ts ↔ boot.ts
// безопасен для esbuild/Vite (то же ограничение, что у любого ESM-цикла).
import { enterFullBleedView, mountFullBleedHeader, exitFullBleedView } from '../main.js';
import { isSetlistPath, parseWordsSetlistId, parseGuestSetlist } from '../router.js';
import { mountAudioSetlistScreen, mountGuestSetlistScreen, mountWordsSetlistScreen } from './route.js';
import { legacyLinkRedirectPath } from './text-link-redirect.js';
import { mountLoginGate } from '../ui/login-gate.js';
import { buildEmptyState } from '../ui/empty-state.js';
import type { IconName } from '../ui/icons.js';
import { t } from '../ui/i18n.js';
import { readOfflineUserId, openOfflineStore } from '../offline/open-store.js';
import type { AppHeaderHandle } from '../ui/app-header.js';

export interface UnifiedSetlistBootCtx {
  appHost: HTMLElement;
  fetchImpl: typeof fetch;
}

/**
 * Возвращает `null` когда `location.pathname`/`search` не подходят ни под
 * один из трёх входов — `boot()` продолжает свою обычную цепочку маршрутов.
 * Иначе рисует экран и возвращает его `unmount`.
 */
export async function bootUnifiedSetlistRoute(ctx: UnifiedSetlistBootCtx): Promise<{ unmount(): void } | null> {
  const { appHost, fetchImpl } = ctx;
  const setlistMatch = isSetlistPath(location.pathname);
  const wordsSetlistId = setlistMatch ? null : parseWordsSetlistId(location.pathname);
  const guestSetlist = setlistMatch || wordsSetlistId ? null : parseGuestSetlist(location.pathname, location.search);
  if (!setlistMatch && !wordsSetlistId && !guestSetlist) return null;

  enterFullBleedView('setlist', appHost);
  const API_BASE = '';
  const navigate = (path: string): void => { if (typeof location !== 'undefined') location.assign(path); };
  let headerHandle: AppHeaderHandle | null = null;
  let screen: { unmount(): void } | null = null;
  const showEmpty = (ic: IconName, title: string, body: string): void => {
    headerHandle = mountFullBleedHeader(appHost, API_BASE, navigate, (content) => {
      content.appendChild(buildEmptyState({
        icon: ic, title, body, dataRole: 'empty-state',
        action: { label: t('common.backToLibrary'), onClick: () => navigate('/library') },
      }));
    });
  };
  const renderScreen = async (): Promise<void> => {
    // Старая ссылка слов (задача D1) — проверяем ПЕРЕД прежней веткой:
    // библиотека уже знает сетлист (участник) или у него нашёлся гостевой
    // ключ → уводим на унифицированный /s/<id>, не рисуя старый экран вовсе.
    if (wordsSetlistId) {
      const legacyPath = await legacyLinkRedirectPath(wordsSetlistId, API_BASE, fetchImpl);
      if (legacyPath) { if (typeof location !== 'undefined') location.replace(legacyPath); return; }
    }
    // Монтируем в отсоединённый узел ДО того, как решаем, что показать: сеть
    // (сетлист/библиотека/лайв) может ответить needs-auth/error, и тогда
    // шапка (app-header) не должна успеть мелькнуть — ровно как у старой
    // ветки, где fetchPublishedSetlist ждали раньше mountFullBleedHeader.
    const detached = document.createElement('div');
    const outcome = setlistMatch
      ? await mountAudioSetlistScreen(detached, setlistMatch.setlistId, { apiBase: API_BASE, navigate, fetchImpl })
      : guestSetlist
        ? await mountGuestSetlistScreen({
            root: detached, setlistId: guestSetlist.setlistId, guestKey: guestSetlist.guestKey,
            deps: { apiBase: API_BASE, navigate, fetchImpl, userId: readOfflineUserId(), offlineStore: openOfflineStore() },
          })
        : await mountWordsSetlistScreen(detached, wordsSetlistId as string, { apiBase: API_BASE, navigate, fetchImpl });
    if (outcome.state === 'mounted') {
      screen = outcome;
      headerHandle = mountFullBleedHeader(appHost, API_BASE, navigate, (content) => { content.appendChild(detached); });
      return;
    }
    if (outcome.state === 'needs-auth') {
      mountLoginGate(appHost, {
        teamName: null,
        apiBase: API_BASE,
        context: 'portal',
        onAuthed: async () => { appHost.innerHTML = ''; await renderScreen(); },
      });
      return;
    }
    if (outcome.state === 'not-found') {
      showEmpty('alert', t('setlist.notFound'), t('setlist.notFoundBody'));
      return;
    }
    if (outcome.state === 'redirect') {
      // replace(), не navigate(): ссылка была не той (чужая/завершённая/
      // склеена с другим сетлистом) — уводим читателя туда, где ответ есть,
      // и не оставляем недействующий /setlist/w/<uuid> в истории назад.
      if (typeof location !== 'undefined' && typeof location.replace === 'function') location.replace(outcome.to);
      return;
    }
    showEmpty('alert', t('setlist.loadError'), t('setlist.loadErrorBody'));
  };
  await renderScreen();
  return { unmount: () => { screen?.unmount(); headerHandle?.unmount(); appHost.innerHTML = ''; exitFullBleedView(); } };
}
